import { ChangeDetectorRef, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { Match, MatchGoal } from '../../../models/match';
import { Team } from '../../../models/team';
import { TournamentGroup } from '../../../models/tournament';
import { MatchService } from '../../../services/match.service';
import { AdminRole } from '../../../services/auth.service';

@Component({
  standalone: false,
  selector: 'app-match-admin',
  template: "<app-loading-overlay [show]=\"isSaving || deletingMatchIds.size > 0 || settingLiveMatchIds.size > 0 || finishingMatchIds.size > 0\"></app-loading-overlay>\n\n<details class=\"card accordion-card\" open>\n  <summary>\n    <span>Calendario partite</span>\n    <small>{{ matches.length }} partite</small>\n  </summary>\n\n  <div class=\"accordion-body\">\n    <div class=\"section-actions\" *ngIf=\"canManageSchedule()\">\n      <button type=\"button\" (click)=\"openCreateMatchModal()\" [disabled]=\"teams.length < 2 || isSaving\">Crea partita</button>\n    </div>\n\n    <p class=\"hint\" *ngIf=\"canManageSchedule() && teams.length < 2\">Servono almeno due squadre per creare una partita.</p>\n    <p class=\"hint\" *ngIf=\"canManageSchedule() && groups.length === 0\">Crea almeno un girone per programmare partite di girone.</p>\n    <p class=\"hint\" *ngIf=\"canManageSchedule()\">Per correggere lo stato di una partita usa esclusivamente il pulsante <strong>Modifica</strong>.</p>\n    <p class=\"error-message\" *ngIf=\"errorMessage\">{{ errorMessage }}</p>\n    <p class=\"success-message\" *ngIf=\"successMessage\">{{ successMessage }}</p>\n    <p class=\"loading-message\" *ngIf=\"isSaving || deletingMatchIds.size || settingLiveMatchIds.size || finishingMatchIds.size\">Operazione in corso, attendere...</p>\n\n    <div class=\"match-list\">\n      <div *ngFor=\"let match of orderedMatches()\" class=\"list-item match-item\" [class.is-live]=\"match.status === 'live'\" [class.is-finished]=\"match.status === 'finished'\">\n        <div class=\"item-main\">\n          <div class=\"match-title-row\">\n            <strong>{{ matchTeamLabel(match, 'A') }} {{ match.score_a }} - {{ match.score_b }} {{ matchTeamLabel(match, 'B') }}</strong>\n            <span class=\"status-badge finished-badge\" *ngIf=\"match.status === 'finished'\">TERMINATA</span>\n            <span class=\"status-badge live-badge\" *ngIf=\"match.status === 'live'\">LIVE</span>\n          </div>\n          <small *ngIf=\"match.shootout_score_a !== null && match.shootout_score_a !== undefined\">Rigori: {{ match.shootout_score_a }} - {{ match.shootout_score_b }}</small>\n          <span>{{ match.phase === 'group' ? match.group_name : match.knockout_round }} - {{ statusLabel(match.status) }} - {{ match.scheduled_at ? (match.scheduled_at | date: 'd MMMM y, HH:mm') : 'Data da definire' }}</span>\n        </div>\n        <div class=\"list-actions\">\n          <button (click)=\"setLive(match)\" [disabled]=\"match.status === 'live' || match.status === 'finished' || !hasBothTeams(match) || isSettingLive(match.id)\" [title]=\"match.status === 'finished' ? 'Per riaprire la partita cambia lo stato dalla sezione Modifica' : ''\">\n            {{ isSettingLive(match.id) ? 'Attendere...' : 'Set Live' }}\n          </button>\n          <button class=\"secondary\" (click)=\"editMatch(match)\" [disabled]=\"!canEditMatch(match) || isSaving || isDeletingMatch(match.id)\">Modifica</button>\n          <button class=\"danger\" *ngIf=\"canManageSchedule()\" (click)=\"deleteMatch(match)\" [disabled]=\"isDeletingMatch(match.id)\">\n            {{ isDeletingMatch(match.id) ? 'Cancellazione...' : 'Elimina' }}\n          </button>\n        </div>\n      </div>\n    </div>\n  </div>\n</details>\n\n<div class=\"modal-backdrop\" *ngIf=\"matchModalOpen\">\n  <section class=\"modal-panel wide\">\n    <div class=\"modal-heading\">\n      <h2>{{ editingMatch ? 'Modifica partita' : 'Nuova partita' }}</h2>\n      <button type=\"button\" class=\"secondary icon-button\" (click)=\"closeMatchModal()\">Chiudi</button>\n    </div>\n\n    <form class=\"form-grid\" (submit)=\"$event.preventDefault(); saveMatch();\">\n      <div class=\"two-columns\">\n        <label>\n          Squadra A\n          <select [(ngModel)]=\"matchForm.team_a_id\" name=\"teamA\" [disabled]=\"!canManageSchedule()\">\n            <option [ngValue]=\"0\" [disabled]=\"matchForm.phase === 'group'\">Da definire</option>\n            <option *ngFor=\"let team of availableTeams()\" [ngValue]=\"team.id\">{{ team.name }}</option>\n          </select>\n        </label>\n\n        <label>\n          Squadra B\n          <select [(ngModel)]=\"matchForm.team_b_id\" name=\"teamB\" [disabled]=\"!canManageSchedule()\">\n            <option [ngValue]=\"0\" [disabled]=\"matchForm.phase === 'group'\">Da definire</option>\n            <option *ngFor=\"let team of availableTeams()\" [ngValue]=\"team.id\">{{ team.name }}</option>\n          </select>\n        </label>\n      </div>\n\n      <label>\n        Data e ora\n        <input type=\"datetime-local\" [(ngModel)]=\"matchForm.scheduled_at\" name=\"scheduledAt\" [disabled]=\"!canManageSchedule()\" />\n      </label>\n\n      <div class=\"two-columns\">\n        <label>\n          Fase\n          <select [(ngModel)]=\"matchForm.phase\" name=\"phase\" (ngModelChange)=\"onPhaseChange()\" [disabled]=\"!canManageSchedule()\">\n            <option value=\"group\">Girone</option>\n            <option value=\"knockout\">Eliminazione diretta</option>\n          </select>\n        </label>\n\n        <label *ngIf=\"matchForm.phase === 'group'\">\n          Girone\n          <select [(ngModel)]=\"matchForm.group_id\" name=\"groupId\" (ngModelChange)=\"onGroupChange()\" [disabled]=\"!canManageSchedule()\">\n            <option [ngValue]=\"null\" disabled>Seleziona girone</option>\n            <option *ngFor=\"let group of groups\" [ngValue]=\"group.id\">{{ group.name }}</option>\n          </select>\n        </label>\n\n        <label *ngIf=\"matchForm.phase === 'knockout'\">\n          Turno\n          <select [(ngModel)]=\"matchForm.knockout_round\" name=\"knockoutRound\" [disabled]=\"!canManageSchedule()\">\n            <option value=\"Sedicesimi\">Sedicesimi</option>\n            <option value=\"Ottavi\">Ottavi</option>\n            <option value=\"Quarti\">Quarti</option>\n            <option value=\"Semifinale\">Semifinale</option>\n            <option value=\"Finale\">Finale</option>\n            <option value=\"Finalina 3 posto\">Finalina 3 posto</option>\n          </select>\n        </label>\n      </div>\n\n      <div class=\"three-columns\">\n        <label>\n          Goal A\n          <input type=\"number\" [(ngModel)]=\"matchForm.score_a\" name=\"scoreA\" min=\"0\" [readonly]=\"!!editingMatch\" />\n        </label>\n        <label>\n          Goal B\n          <input type=\"number\" [(ngModel)]=\"matchForm.score_b\" name=\"scoreB\" min=\"0\" [readonly]=\"!!editingMatch\" />\n        </label>\n        <label>\n          Stato\n          <select [(ngModel)]=\"matchForm.status\" name=\"status\" [disabled]=\"!canManageSchedule()\">\n            <option value=\"draft\">Da definire</option>\n            <option value=\"scheduled\">Programmata</option>\n            <option value=\"live\">Live</option>\n            <option value=\"finished\">Terminata</option>\n          </select>\n        </label>\n        <label *ngIf=\"matchForm.phase === 'knockout'\">\n          Ordine tabellone\n          <input type=\"number\" [(ngModel)]=\"matchForm.knockout_order\" name=\"knockoutOrder\" min=\"1\" step=\"1\" [disabled]=\"!canManageSchedule()\" />\n        </label>\n      </div>\n\n      <div class=\"two-columns\" *ngIf=\"matchForm.phase === 'group' && selectedGroup()?.shootouts_enabled && matchForm.score_a === matchForm.score_b\">\n        <label>Rigori squadra A<input type=\"number\" [(ngModel)]=\"matchForm.shootout_score_a\" name=\"shootoutScoreA\" min=\"0\" /></label>\n        <label>Rigori squadra B<input type=\"number\" [(ngModel)]=\"matchForm.shootout_score_b\" name=\"shootoutScoreB\" min=\"0\" /></label>\n      </div>\n\n      <section class=\"goals-editor\" *ngIf=\"editingMatch && canEditGoals()\">\n        <div class=\"goals-heading\">\n          <div>\n            <strong>Goal partita</strong>\n            <small>Il risultato viene calcolato dai goal inseriti.</small>\n          </div>\n          <span>{{ matchForm.score_a }} - {{ matchForm.score_b }}</span>\n        </div>\n\n        <div class=\"goal-row add-goal\">\n          <label>\n            Goal per\n            <select [(ngModel)]=\"newGoal.team_id\" name=\"newGoalTeam\" (ngModelChange)=\"onNewGoalChange()\">\n              <option [ngValue]=\"matchForm.team_a_id\">{{ teamById(matchForm.team_a_id || 0)?.name || 'Squadra A' }}</option>\n              <option [ngValue]=\"matchForm.team_b_id\">{{ teamById(matchForm.team_b_id || 0)?.name || 'Squadra B' }}</option>\n            </select>\n          </label>\n\n          <label>\n            Tipo\n            <select [(ngModel)]=\"newGoal.is_own_goal\" name=\"newGoalType\" (ngModelChange)=\"onNewGoalChange()\">\n              <option [ngValue]=\"false\">Goal</option>\n              <option [ngValue]=\"true\">Autogol</option>\n            </select>\n          </label>\n\n          <label>\n            Giocatore\n            <select [(ngModel)]=\"newGoal.player_id\" name=\"newGoalPlayer\">\n              <option [ngValue]=\"0\" disabled>Seleziona giocatore</option>\n              <option *ngFor=\"let player of playersForGoal()\" [ngValue]=\"player.id\">{{ player.name }}</option>\n            </select>\n          </label>\n\n          <button type=\"button\" class=\"secondary\" (click)=\"addGoal()\">Aggiungi goal</button>\n        </div>\n\n        <div class=\"goal-list\" *ngIf=\"matchGoals.length; else noGoals\">\n          <div class=\"goal-item\" *ngFor=\"let goal of matchGoals; let i = index\">\n            <span>{{ i + 1 }}. {{ goalDescription(goal) }}</span>\n            <button type=\"button\" class=\"danger\" (click)=\"removeGoal(i)\">Rimuovi</button>\n          </div>\n        </div>\n\n        <ng-template #noGoals>\n          <p class=\"hint\">Nessun goal registrato per questa partita.</p>\n        </ng-template>\n      </section>\n\n      <p class=\"error-message\" *ngIf=\"errorMessage\">{{ errorMessage }}</p>\n      <p class=\"loading-message\" *ngIf=\"isSaving\">Salvataggio in corso...</p>\n\n      <div class=\"actions\">\n        <button type=\"submit\" [disabled]=\"isSaving || (matchForm.phase === 'group' && (teams.length < 2 || groups.length === 0)) || (!canManageSchedule() && !canEditGoals())\">\n          {{ isSaving ? 'Salvataggio...' : saveButtonLabel() }}\n        </button>\n        <button type=\"button\" class=\"secondary\" (click)=\"closeMatchModal()\" [disabled]=\"isSaving\">Annulla</button>\n      </div>\n    </form>\n  </section>\n</div>\n",
  styles: [".match-list {\n  display: grid;\n  gap: 0.75rem;\n  margin-top: 1rem;\n}\n\n.two-columns,\n.three-columns {\n  display: grid;\n  gap: 0.75rem;\n}\n\n.two-columns {\n  grid-template-columns: repeat(2, minmax(0, 1fr));\n}\n\n.three-columns {\n  grid-template-columns: repeat(3, minmax(0, 1fr));\n}\n\n.actions {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.6rem;\n}\n\n.hint {\n  color: #facc15;\n  margin: 1rem 0 0;\n}\n\n.goals-editor {\n  border: 1px solid rgba(148, 163, 184, 0.25);\n  border-radius: 8px;\n  display: grid;\n  gap: 0.85rem;\n  grid-column: 1 / -1;\n  padding: 1rem;\n}\n\n.goals-heading,\n.goal-item {\n  align-items: center;\n  display: flex;\n  gap: 0.75rem;\n  justify-content: space-between;\n}\n\n.goals-heading span {\n  color: #b9f6c8;\n  font-size: 1.4rem;\n  font-weight: 800;\n}\n\n.goal-row {\n  align-items: end;\n  display: grid;\n  gap: 0.75rem;\n  grid-template-columns: repeat(3, minmax(0, 1fr)) auto;\n}\n\n.goal-list {\n  display: grid;\n  gap: 0.5rem;\n}\n\n.goal-item {\n  background: rgba(15, 23, 42, 0.55);\n  border-radius: 8px;\n  padding: 0.55rem 0.7rem;\n}\n\n.is-live {\n  border-color: rgba(34, 197, 94, 0.65);\n  box-shadow: inset 4px 0 0 #22c55e;\n}\n\n.match-item {\n  transition: border-color 160ms ease, box-shadow 160ms ease, opacity 160ms ease;\n}\n\n.match-title-row {\n  align-items: center;\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.65rem;\n}\n\n.status-badge {\n  border-radius: 999px;\n  font-size: 0.7rem;\n  font-weight: 900;\n  letter-spacing: 0.08em;\n  padding: 0.28rem 0.55rem;\n}\n\n.live-badge {\n  background: #16a34a;\n  color: white;\n}\n\n.is-finished {\n  background: linear-gradient(90deg, rgba(71, 85, 105, 0.28), rgba(15, 23, 42, 0.5));\n  border-color: rgba(148, 163, 184, 0.5);\n  box-shadow: inset 5px 0 0 #64748b;\n}\n\n.is-finished .item-main > strong,\n.is-finished .match-title-row > strong {\n  color: #e2e8f0;\n}\n\n.is-finished .item-main > span,\n.is-finished .item-main > small {\n  color: #94a3b8;\n}\n\n.finished-badge {\n  background: #475569;\n  border: 1px solid #94a3b8;\n  color: #f8fafc;\n}\n\n@media (max-width: 720px) {\n  .two-columns,\n  .three-columns,\n  .goal-row {\n    grid-template-columns: 1fr;\n  }\n}\n"]
})
export class MatchAdminComponent {
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly matchService = new MatchService();

  @Input() teams: Team[] = [];
  @Input() groups: TournamentGroup[] = [];
  @Input() matches: Match[] = [];
  @Input() role: AdminRole | null = null;
  @Output() changed = new EventEmitter<void>();

  matchForm: Partial<Match> = {
    team_a_id: 0,
    team_b_id: 0,
    scheduled_at: '',
    score_a: 0,
    score_b: 0,
    shootout_score_a: null,
    shootout_score_b: null,
    status: 'draft',
    phase: 'group',
    group_id: null,
    knockout_round: null,
    knockout_order: null
  };
  matchGoals: MatchGoal[] = [];
  newGoal: MatchGoal = { team_id: 0, player_id: 0, is_own_goal: false };
  editingMatch: Match | null = null;
  matchModalOpen = false;
  errorMessage = '';
  successMessage = '';
  isSaving = false;
  deletingMatchIds = new Set<number>();
  settingLiveMatchIds = new Set<number>();
  finishingMatchIds = new Set<number>();

  async saveMatch(): Promise<void> {
    if (this.isSaving) {
      return;
    }

    if (this.role === 'redattore') {
      await this.saveEditorGoals();
      return;
    }

    this.ensureDefaultTeams();

    if (this.matchForm.phase !== 'knockout' && (!this.matchForm.team_a_id || !this.matchForm.team_b_id)) {
      this.errorMessage = 'Compila tutti i campi della partita.';
      return;
    }
    if (this.matchForm.phase === 'group' && !this.matchForm.group_id) {
      this.errorMessage = 'Seleziona il girone della partita.';
      return;
    }
    if (this.matchForm.phase === 'group' && !this.selectedGroupHasTeam(Number(this.matchForm.team_a_id))) {
      this.errorMessage = 'La squadra A non appartiene al girone selezionato.';
      return;
    }
    if (this.matchForm.phase === 'group' && !this.selectedGroupHasTeam(Number(this.matchForm.team_b_id))) {
      this.errorMessage = 'La squadra B non appartiene al girone selezionato.';
      return;
    }
    if (this.matchForm.team_a_id && this.matchForm.team_b_id && Number(this.matchForm.team_a_id) === Number(this.matchForm.team_b_id)) {
      this.errorMessage = 'Le squadre A e B devono essere diverse.';
      return;
    }
    if (this.matchForm.phase === 'knockout' && ['live', 'finished'].includes(String(this.matchForm.status)) && (!this.matchForm.team_a_id || !this.matchForm.team_b_id)) {
      this.errorMessage = 'Per mettere live o terminare una partita di fase finale servono entrambe le squadre.';
      return;
    }
    if (Number(this.matchForm.score_a) < 0 || Number(this.matchForm.score_b) < 0) {
      this.errorMessage = 'Il risultato non puo avere valori negativi.';
      return;
    }

    try {
      this.isSaving = true;
      this.errorMessage = '';
      this.successMessage = '';
      const goals = this.normalizedGoals();
      const payload = {
        team_a_id: this.matchForm.phase === 'knockout' && !this.matchForm.team_a_id ? null : Number(this.matchForm.team_a_id),
        team_b_id: this.matchForm.phase === 'knockout' && !this.matchForm.team_b_id ? null : Number(this.matchForm.team_b_id),
        scheduled_at: this.matchForm.scheduled_at || null,
        score_a: this.editingMatch ? this.scoreForTeam(Number(this.matchForm.team_a_id), goals) : Number(this.matchForm.score_a ?? 0),
        score_b: this.editingMatch ? this.scoreForTeam(Number(this.matchForm.team_b_id), goals) : Number(this.matchForm.score_b ?? 0),
        shootout_score_a: this.matchForm.shootout_score_a ?? null,
        shootout_score_b: this.matchForm.shootout_score_b ?? null,
        status: this.matchForm.status ?? 'draft',
        phase: this.matchForm.phase ?? 'group',
        group_id: this.matchForm.phase === 'group' ? Number(this.matchForm.group_id) : null,
        knockout_round: this.matchForm.phase === 'knockout' ? (this.matchForm.knockout_round || 'Finale') : null,
        knockout_order: this.matchForm.phase === 'knockout' ? Number(this.matchForm.knockout_order || 1) : null,
        goals: this.editingMatch ? goals : undefined
      };

      if (this.editingMatch) {
        await this.matchService.update(this.editingMatch.id, payload);
      } else {
        await this.matchService.create(payload);
      }

      this.matchModalOpen = false;
      this.successMessage = 'Operazione conclusa. Partita salvata.';
      this.resetMatchForm();
      this.changed.emit();
      this.changeDetector.detectChanges();
    } catch (error) {
      this.successMessage = '';
      this.errorMessage = this.readError(error, 'Errore durante il salvataggio della partita.');
      this.changeDetector.detectChanges();
    } finally {
      this.isSaving = false;
      this.changeDetector.detectChanges();
    }
  }

  editMatch(match: Match): void {
    this.editingMatch = match;
    this.matchForm = {
      team_a_id: match.team_a_id,
      team_b_id: match.team_b_id,
      scheduled_at: this.toDateTimeLocal(match.scheduled_at),
      score_a: match.score_a,
      score_b: match.score_b,
      shootout_score_a: match.shootout_score_a ?? null,
      shootout_score_b: match.shootout_score_b ?? null,
      status: match.status,
      phase: match.phase,
      group_id: match.group_id ?? null,
      knockout_round: match.knockout_round ?? null,
      knockout_order: match.knockout_order ?? null
    };
    this.matchGoals = this.normalizedExistingGoals(match.goals ?? []);
    this.resetNewGoal();
    this.matchModalOpen = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.changeDetector.detectChanges();
  }

  resetMatchForm(): void {
    this.editingMatch = null;
    this.matchForm = { team_a_id: 0, team_b_id: 0, scheduled_at: '', score_a: 0, score_b: 0, status: 'draft', phase: 'group', group_id: null, knockout_round: null, knockout_order: null };
    this.matchGoals = [];
    this.resetNewGoal();
    this.ensureDefaultTeams();
    this.ensureDefaultGroup();
    this.errorMessage = '';
    this.changeDetector.detectChanges();
  }

  async deleteMatch(match: Match): Promise<void> {
    if (!this.canManageSchedule()) {
      return;
    }

    if (this.deletingMatchIds.has(match.id)) {
      return;
    }

    if (!window.confirm(`Confermi la cancellazione della partita "${this.matchTeamLabel(match, 'A')} - ${this.matchTeamLabel(match, 'B')}"?`)) {
      return;
    }

    try {
      this.deletingMatchIds.add(match.id);
      this.errorMessage = '';
      this.successMessage = '';
      await this.matchService.delete(match.id);
      this.matches = this.matches.filter((item) => item.id !== match.id);
      this.successMessage = 'Operazione conclusa. Partita cancellata.';
      this.changed.emit();
      this.changeDetector.detectChanges();
    } catch (error) {
      this.successMessage = '';
      this.errorMessage = this.readError(error, 'Errore durante la cancellazione della partita.');
      this.changeDetector.detectChanges();
    } finally {
      this.deletingMatchIds.delete(match.id);
      this.changeDetector.detectChanges();
    }
  }

  async setLive(match: Match): Promise<void> {
    if (this.settingLiveMatchIds.has(match.id)) {
      return;
    }

    if (match.status === 'finished') {
      this.errorMessage = 'Una partita terminata non può essere impostata nuovamente live. Cambia lo stato dalla sezione Modifica.';
      this.changeDetector.detectChanges();
      return;
    }

    if (!this.hasBothTeams(match)) {
      this.errorMessage = 'La partita non puo andare live finche entrambi gli slot del tabellone non sono assegnati.';
      this.changeDetector.detectChanges();
      return;
    }

    try {
      this.settingLiveMatchIds.add(match.id);
      this.errorMessage = '';
      this.successMessage = '';
      await this.matchService.setLive(match.id);
      this.matches = this.matches.map((item) => {
        if (item.id === match.id) {
          return { ...item, status: 'live' };
        }
        if (item.status === 'live') {
          return { ...item, status: 'scheduled' };
        }
        return item;
      });
      this.successMessage = 'Operazione conclusa. Partita impostata live.';
      this.changed.emit();
      this.changeDetector.detectChanges();
    } catch (error) {
      this.successMessage = '';
      this.errorMessage = this.readError(error, 'Errore durante il set live.');
      this.changeDetector.detectChanges();
    } finally {
      this.settingLiveMatchIds.delete(match.id);
      this.changeDetector.detectChanges();
    }
  }

  async finishMatch(match: Match): Promise<void> {
    if (match.status !== 'live') {
      return;
    }

    if (this.needsLiveShootoutManagement(match)) {
      this.errorMessage = 'La partita è in parità e il girone prevede i rigori. Apri la sezione Live per concludere i tempi regolamentari e gestirli.';
      this.changeDetector.detectChanges();
      return;
    }

    if (this.finishingMatchIds.has(match.id)) {
      return;
    }

    if (!window.confirm(`Confermi di terminare la partita "${this.matchTeamLabel(match, 'A')} - ${this.matchTeamLabel(match, 'B')}"?`)) {
      return;
    }

    try {
      this.finishingMatchIds.add(match.id);
      this.errorMessage = '';
      this.successMessage = '';
      await this.matchService.finish(match.id);
      this.matches = this.matches.map((item) => item.id === match.id ? { ...item, status: 'finished' } : item);
      this.successMessage = 'Operazione conclusa. Partita terminata.';
      this.changed.emit();
      this.changeDetector.detectChanges();
    } catch (error) {
      this.successMessage = '';
      this.errorMessage = this.readError(error, 'Errore durante la chiusura della partita.');
      this.changeDetector.detectChanges();
    } finally {
      this.finishingMatchIds.delete(match.id);
      this.changeDetector.detectChanges();
    }
  }

  needsLiveShootoutManagement(match: Match): boolean {
    return match.phase === 'group'
      && Number(match.group_shootouts_enabled) === 1
      && Number(match.score_a) === Number(match.score_b);
  }

  statusLabel(status: Match['status']): string {
    const labels: Record<Match['status'], string> = {
      draft: 'Da definire',
      scheduled: 'Programmata',
      live: 'Live',
      finished: 'Terminata'
    };
    return labels[status];
  }

  availableTeams(): Team[] {
    if (this.matchForm.phase !== 'group') {
      return this.teams;
    }

    const selectedGroup = this.selectedGroup();
    return selectedGroup?.teams ?? [];
  }

  onPhaseChange(): void {
    if (this.matchForm.phase === 'group') {
      this.ensureDefaultGroup();
      this.ensureDefaultTeamsForSelectedGroup();
    } else {
      this.ensureDefaultTeams();
    }
    this.errorMessage = '';
    this.changeDetector.detectChanges();
  }

  onGroupChange(): void {
    this.ensureDefaultTeamsForSelectedGroup();
    this.errorMessage = '';
    this.changeDetector.detectChanges();
  }

  orderedMatches(): Match[] {
    return [...this.matches].sort((a, b) => {
      if (a.scheduled_at && b.scheduled_at) {
        return a.scheduled_at.localeCompare(b.scheduled_at);
      }
      if (a.scheduled_at) {
        return -1;
      }
      if (b.scheduled_at) {
        return 1;
      }
      return a.id - b.id;
    });
  }

  openCreateMatchModal(): void {
    if (!this.canManageSchedule()) {
      return;
    }

    this.resetMatchForm();
    this.matchModalOpen = true;
    this.changeDetector.detectChanges();
  }

  closeMatchModal(): void {
    this.matchModalOpen = false;
    this.resetMatchForm();
  }

  addGoal(): void {
    const teamId = Number(this.newGoal.team_id);
    const playerId = Number(this.newGoal.player_id);
    if (!teamId || !playerId) {
      this.errorMessage = 'Seleziona squadra e giocatore del goal.';
      return;
    }

    this.matchGoals = [...this.matchGoals, {
      team_id: teamId,
      player_id: playerId,
      is_own_goal: Boolean(this.newGoal.is_own_goal)
    }];
    this.syncScoreFromGoals();
    this.resetNewGoal();
    this.errorMessage = '';
    this.changeDetector.detectChanges();
  }

  removeGoal(index: number): void {
    this.matchGoals = this.matchGoals.filter((_, goalIndex) => goalIndex !== index);
    this.syncScoreFromGoals();
    this.changeDetector.detectChanges();
  }

  playersForGoal(): { id?: number; name: string }[] {
    const teamId = Number(this.newGoal.team_id);
    if (!teamId) {
      return [];
    }

    const playerTeamId = this.newGoal.is_own_goal ? this.opponentTeamId(teamId) : teamId;
    return this.teamById(playerTeamId)?.players ?? [];
  }

  onNewGoalChange(): void {
    const availablePlayers = this.playersForGoal();
    if (!availablePlayers.some((player) => player.id === Number(this.newGoal.player_id))) {
      this.newGoal.player_id = Number(availablePlayers[0]?.id ?? 0);
    }
    this.changeDetector.detectChanges();
  }

  goalDescription(goal: MatchGoal): string {
    const team = this.teamById(Number(goal.team_id))?.name ?? goal.team_name ?? 'Squadra';
    const player = this.playerName(Number(goal.player_id)) ?? goal.player_name ?? 'Giocatore';
    return Boolean(goal.is_own_goal) ? `${team}: autogol ${player}` : `${team}: ${player}`;
  }

  canManageSchedule(): boolean {
    return this.role === 'super_user' || this.role === 'organizzatore';
  }

  canEditMatch(match: Match): boolean {
    return this.canManageSchedule() || match.status === 'live';
  }

  canEditGoals(): boolean {
    return Boolean(this.editingMatch?.team_a_id && this.editingMatch?.team_b_id) && (this.canManageSchedule() || this.editingMatch?.status === 'live');
  }

  hasBothTeams(match: Match): boolean {
    return Boolean(match.team_a_id && match.team_b_id);
  }

  isDeletingMatch(matchId: number): boolean {
    return this.deletingMatchIds.has(matchId);
  }

  isSettingLive(matchId: number): boolean {
    return this.settingLiveMatchIds.has(matchId);
  }

  isFinishingMatch(matchId: number): boolean {
    return this.finishingMatchIds.has(matchId);
  }

  matchTeamLabel(match: Match, slot: 'A' | 'B'): string {
    if (slot === 'A') {
      return match.team_a_name || match.bracket_slot_a || 'Da definire';
    }
    return match.team_b_name || match.bracket_slot_b || 'Da definire';
  }

  saveButtonLabel(): string {
    if (this.role === 'redattore') {
      return 'Salva goal live';
    }
    return this.editingMatch ? 'Aggiorna partita' : 'Crea partita';
  }

  private async saveEditorGoals(): Promise<void> {
    if (!this.editingMatch || this.editingMatch.status !== 'live') {
      this.errorMessage = 'Il redattore puo aggiornare solo una partita live.';
      this.changeDetector.detectChanges();
      return;
    }

    try {
      this.isSaving = true;
      this.errorMessage = '';
      this.successMessage = '';
      await this.matchService.setGoals(this.editingMatch.id, this.normalizedGoals());
      this.matchModalOpen = false;
      this.successMessage = 'Operazione conclusa. Goal salvati.';
      this.resetMatchForm();
      this.changed.emit();
      this.changeDetector.detectChanges();
    } catch (error) {
      this.successMessage = '';
      this.errorMessage = this.readError(error, 'Errore durante il salvataggio dei goal.');
      this.changeDetector.detectChanges();
    } finally {
      this.isSaving = false;
      this.changeDetector.detectChanges();
    }
  }

  private ensureDefaultTeams(): void {
    if (!this.teams.length) {
      return;
    }

    if (this.matchForm.phase === 'group') {
      this.ensureDefaultGroup();
      this.ensureDefaultTeamsForSelectedGroup();
      return;
    }

    if (!this.matchForm.team_a_id) {
      this.matchForm.team_a_id = this.teams[0].id;
    }
    if (!this.matchForm.team_b_id && this.teams.length > 1) {
      this.matchForm.team_b_id = this.teams[1].id;
    }
    this.ensureDefaultGroup();
  }

  private resetNewGoal(): void {
    const teamId = Number(this.matchForm.team_a_id || 0);
    this.newGoal = { team_id: teamId, player_id: 0, is_own_goal: false };
    this.onNewGoalChange();
  }

  private syncScoreFromGoals(): void {
    this.matchForm.score_a = this.scoreForTeam(Number(this.matchForm.team_a_id), this.matchGoals);
    this.matchForm.score_b = this.scoreForTeam(Number(this.matchForm.team_b_id), this.matchGoals);
  }

  private scoreForTeam(teamId: number, goals: MatchGoal[]): number {
    return goals.filter((goal) => Number(goal.team_id) === teamId).length;
  }

  private normalizedGoals(): MatchGoal[] {
    return this.matchGoals.map((goal) => ({
      team_id: Number(goal.team_id),
      player_id: Number(goal.player_id),
      is_own_goal: Boolean(goal.is_own_goal)
    }));
  }

  private normalizedExistingGoals(goals: MatchGoal[]): MatchGoal[] {
    return goals.map((goal) => ({
      team_id: Number(goal.team_id),
      player_id: Number(goal.player_id),
      is_own_goal: Boolean(goal.is_own_goal)
    }));
  }

  private opponentTeamId(teamId: number): number {
    const teamA = Number(this.matchForm.team_a_id);
    const teamB = Number(this.matchForm.team_b_id);
    return teamId === teamA ? teamB : teamA;
  }

  teamById(teamId: number): Team | undefined {
    return this.teams.find((team) => team.id === teamId);
  }

  private playerName(playerId: number): string | null {
    for (const team of this.teams) {
      const player = team.players?.find((item) => item.id === playerId);
      if (player) {
        return player.name;
      }
    }
    return null;
  }

  private ensureDefaultGroup(): void {
    if (this.matchForm.phase === 'group' && !this.matchForm.group_id && this.groups.length) {
      this.matchForm.group_id = this.groups[0].id;
    }
  }

  private ensureDefaultTeamsForSelectedGroup(): void {
    const groupTeams = this.availableTeams();
    if (!groupTeams.length) {
      this.matchForm.team_a_id = 0;
      this.matchForm.team_b_id = 0;
      return;
    }

    if (!this.matchForm.team_a_id || !groupTeams.some((team) => team.id === Number(this.matchForm.team_a_id))) {
      this.matchForm.team_a_id = groupTeams[0].id;
    }
    if (!this.matchForm.team_b_id || !groupTeams.some((team) => team.id === Number(this.matchForm.team_b_id))) {
      this.matchForm.team_b_id = groupTeams.length > 1 ? groupTeams[1].id : 0;
    }
  }

  selectedGroup(): TournamentGroup | undefined {
    return this.groups.find((group) => group.id === Number(this.matchForm.group_id));
  }

  private selectedGroupHasTeam(teamId: number): boolean {
    return this.selectedGroup()?.teams.some((team) => team.id === teamId) ?? false;
  }

  private toDateTimeLocal(value: string | null): string {
    return value ? value.replace(' ', 'T').slice(0, 16) : '';
  }

  private readError(error: unknown, fallback: string): string {
    return error instanceof Error ? error.message : fallback;
  }
}
